import React, { createContext, useContext, useState, useCallback } from 'react';
import { adminApi } from '../services/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState({
    customers: false,
    orders: false,
    analytics: false,
  });

  // Fetch customers with pagination and filters
  const fetchCustomers = useCallback(async (params = {}) => {
    setLoading(prev => ({ ...prev, customers: true }));
    try {
      const response = await adminApi.getCustomers(params);
      setCustomers(response); // response is the array of customers
      return response;
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, customers: false }));
    }
  }, []);

  // Fetch orders with pagination and filters
  const fetchOrders = useCallback(async (params = {}) => {
    setLoading(prev => ({ ...prev, orders: true }));
    try {
      const response = await adminApi.getOrders(params);
      setOrders(response); // response is the array of orders
      return response;
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  }, []);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (params = {}) => {
    setLoading(prev => ({ ...prev, analytics: true }));
    try {
      const response = await adminApi.getAnalytics(params);
      if (response.success) {
        setAnalytics(response.data);
        return response;
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, analytics: false }));
    }
  }, []);

  // Get customer details with Stripe data
  const getCustomerDetails = useCallback(async (customerId) => {
    try {
      const response = await adminApi.getCustomerDetails(customerId);
      return response;
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      throw error;
    }
  }, []);

  // Get order details with Shippo data
  const getOrderDetails = useCallback(async (orderId) => {
    try {
      const response = await adminApi.getOrderDetails(orderId);
      return response;
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      throw error;
    }
  }, []);

  // Update order status
  const updateOrderStatus = useCallback(async (orderId, status) => {
    try {
      const response = await adminApi.updateOrderStatus(orderId, status);
      if (response.success) {
        // Update local orders state
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, status } : order
        ));
      }
      return response;
    } catch (error) {
      console.error('Failed to update order status:', error);
      throw error;
    }
  }, []);

  // Refund order
  const refundOrder = useCallback(async (orderId, amount) => {
    try {
      const response = await adminApi.refundOrder(orderId, amount);
      if (response.success) {
        // Update local orders state
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, status: 'refunded' } : order
        ));
      }
      return response;
    } catch (error) {
      console.error('Failed to refund order:', error);
      throw error;
    }
  }, []);

  // Refresh all data
  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchCustomers(),
      fetchOrders(),
      fetchAnalytics(),
    ]);
  }, [fetchCustomers, fetchOrders, fetchAnalytics]);

  const value = {
    customers,
    orders,
    analytics,
    loading,
    fetchCustomers,
    fetchOrders,
    fetchAnalytics,
    getCustomerDetails,
    getOrderDetails,
    updateOrderStatus,
    refundOrder,
    refreshData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
